import type { Suit, CardData } from './types';
import { instantiateMission } from './missions';
import { effectsForCard } from './effects';

export function generateSuitCards(
  suit: Suit,
  identifierPrefix: string,
  prng: () => number,
  usedMissionTemplates?: Set<string>
): CardData[] {
  const cards: CardData[] = [];
  // 1 to 6 Hand cards. Effects (if any are bound for this (suit, value)) are
  // attached here at deck-build time and travel with the card identity.
  for (let i = 1; i <= 6; i++) {
    const cardId = `${identifierPrefix}_${suit}_${i}`;
    // Procedurally-generated decks (bot / fallback when no user deck supplied)
    // are all street — kingpin/made cards are earned, not procgen'd.
    const effects = effectsForCard(suit, i, 'street', cardId);
    cards.push({
      id: cardId,
      suit,
      value: i,
      type: 'Hand',
      rarity: 'street',
      ...(effects ? { effects } : {}),
    });
  }
  // 1 Mission card with a randomly instantiated mission rule (avoiding
  // templates already used by other suit slots in the same match if a
  // shared exclusion set is passed in).
  const mission = instantiateMission(suit, prng, identifierPrefix, usedMissionTemplates);
  usedMissionTemplates?.add(mission.templateId);
  cards.push({
    id: `${identifierPrefix}_${suit}_mission`,
    suit,
    value: 0,
    type: 'Mission',
    rarity: 'street',
    mission,
  });
  return cards;
}

// Pure-data deck composition validator. Same rules as
// `AuthStore.validateDeckCardIds` (which queries the DB), but operates on
// already-loaded CardData[] — usable client-side to gray out invalid decks
// in the picker BEFORE the user can pick one, and server-side at the
// commit-deck endpoint to refuse non-playable decks.
//   - exactly 14 cards
//   - exactly 2 distinct suits
//   - for each suit: 6 Hand cards with values {1,2,3,4,5,6} (no duplicates,
//     no out-of-range)
//   - for each suit: exactly 1 Mission card
export type DeckValidation =
  | { valid: true }
  | { valid: false; reason: string };

export function validateDeckComposition(cards: CardData[]): DeckValidation {
  if (!Array.isArray(cards)) return { valid: false, reason: 'cards is not an array' };
  if (cards.length !== 14) {
    return { valid: false, reason: `14 cards required, got ${cards.length}` };
  }
  const bySuit = new Map<string, { handValues: Set<number>; missions: number }>();
  for (const c of cards) {
    if (!c || typeof c.suit !== 'string') {
      return { valid: false, reason: 'invalid card' };
    }
    const slot = bySuit.get(c.suit) ?? { handValues: new Set<number>(), missions: 0 };
    if (c.type === 'Hand') {
      if (!Number.isInteger(c.value) || c.value < 1 || c.value > 6) {
        return { valid: false, reason: `Hand value ${c.value} out of range 1-6 (suit ${c.suit})` };
      }
      if (slot.handValues.has(c.value)) {
        return { valid: false, reason: `Hand value ${c.value} duplicated for suit ${c.suit}` };
      }
      slot.handValues.add(c.value);
    } else if (c.type === 'Mission') {
      slot.missions += 1;
    } else {
      return { valid: false, reason: `unknown card type "${c.type}"` };
    }
    bySuit.set(c.suit, slot);
  }
  if (bySuit.size !== 2) {
    return { valid: false, reason: `exactly 2 suits required, found ${bySuit.size}` };
  }
  for (const [suit, slot] of bySuit) {
    if (slot.handValues.size !== 6) {
      return { valid: false, reason: `suit ${suit}: 6 Hand 1-6 required, found ${slot.handValues.size}` };
    }
    if (slot.missions !== 1) {
      return { valid: false, reason: `suit ${suit}: exactly 1 Mission required, found ${slot.missions}` };
    }
  }
  return { valid: true };
}

// Utility to shuffle an array (Fisher-Yates)
export function shuffle<T>(array: T[], randomFunc: () => number = Math.random): T[] {
  let [m, t, i] = [array.length, null as any, 0];
  while (m) {
    i = Math.floor(randomFunc() * m--);
    t = array[m];
    array[m] = array[i];
    array[i] = t;
  }
  return array;
}
